import {Alert, Box, Button, Card, Dialog, DialogContent, Grid, MenuItem, Paper, Skeleton, Stack, TextField, Typography} from "@mui/material";
import React, { useState } from "react";
import { Property, PropertyInvoiceWithRelations, useGetInvoicesQuery, usePostInvoicePropertyMutation } from "../../redux/residentPortalApi";
import InvoicesList from "./InvoicesList";
import TopUpModal from "./TopUpModal";
import {formatCurrency} from "../../utils/currencyUtil";
import WalletCard from "./WalletCard";
import {useHasAnyRole} from "../../hooks/useHasAnyRole";

interface OwnProps {
  property: Property;
  invoices: PropertyInvoiceWithRelations[] | undefined;
  outstandingLeviesBalance: number | null;
}

const LeviesSection: React.FC<OwnProps> = ({ property, invoices, outstandingLeviesBalance }) => {
  const [openTopUpDialog, setOpenTopUpDialog] = useState(false);
  const isHoa = useHasAnyRole(['hoa']);
  const [selectedTemplateInvoiceId, setSelectedTemplateInvoiceId] = useState('');
  const [selectedDueDate, setSelectedDueDate] = useState('');
  const [singleInvoiceError, setSingleInvoiceError] = useState('');
  const [singleInvoiceSuccess, setSingleInvoiceSuccess] = useState('');

  const [createSinglePropertyInvoice, {isLoading: isCreatingSingleInvoice}] = usePostInvoicePropertyMutation();
  const {data: templateInvoices} = useGetInvoicesQuery(
    {
      development: property.development,
      phase: property.phase,
      type: property.type,
      limit: 100,
      skip: 0,
      order: 'dueDate DESC',
    },
    {skip: !property.development || !property.phase || !property.type}
  );

  const handleOpenDialog = () => setOpenTopUpDialog(true);
  const handleCloseDialog = () => setOpenTopUpDialog(false);

  const handleCreateSingleInvoice = async () => {
    setSingleInvoiceError('');
    setSingleInvoiceSuccess('');

    if (!selectedTemplateInvoiceId) {
      setSingleInvoiceError('Please select a source invoice.');
      return;
    }

    if (!property.id) {
      setSingleInvoiceError('Property id is required.');
      return;
    }

    if (!(templateInvoices ?? []).some(invoice => invoice.id === selectedTemplateInvoiceId)) {
      setSingleInvoiceError('Could not find the selected source invoice. Please refresh and try again.');
      return;
    }

    try {
      await createSinglePropertyInvoice({
        body: {
          propertyId: property.id,
          templateInvoiceId: selectedTemplateInvoiceId,
          ...(selectedDueDate ? {dueDate: new Date(selectedDueDate).toISOString()} : {}),
        },
      }).unwrap();

      setSingleInvoiceSuccess('Single property levy invoice created successfully.');
    } catch (error) {
      setSingleInvoiceError('Failed to create single property levy invoice. Please try again.');
    }
  };

  const isBalanceNegative = property.walletBalance !== undefined && property.walletBalance < 0;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        mb: 4
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h2" sx={{
          fontSize: '1.75rem',
          fontWeight: 600,
          color: '#1976d2',
          mb: 2,
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: 1
        }}>
          Levies
        </Typography>

        {isHoa && (
          <Paper sx={{p: 2, borderRadius: 2, border: '1px solid #e0e0e0'}}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{fontWeight: 600}}>Create Single Property Levy Invoice</Typography>
              <Typography variant="body2" color="text.secondary">
                Select the latest invoice template for this property's development, phase, and type, then create a levy invoice for this single property.
                Status defaults to Unpaid.
              </Typography>

              <TextField
                select
                label="Source Invoice"
                value={selectedTemplateInvoiceId}
                onChange={e => setSelectedTemplateInvoiceId(e.target.value)}
                fullWidth
              >
                {(templateInvoices ?? []).map(invoice => (
                  <MenuItem key={invoice.id} value={invoice.id ?? ''}>
                    {`Invoice #${invoice.id ?? 'N/A'} | Due ${invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'} | Total ${formatCurrency(invoice.totalCost ?? 0)}`}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Due Date (optional)"
                type="date"
                value={selectedDueDate}
                onChange={e => setSelectedDueDate(e.target.value)}
                InputLabelProps={{shrink: true}}
                fullWidth
              />

              {singleInvoiceError ? <Alert severity="error">{singleInvoiceError}</Alert> : null}
              {singleInvoiceSuccess ? <Alert severity="success">{singleInvoiceSuccess}</Alert> : null}

              <Box>
                <Button
                  variant="contained"
                  onClick={handleCreateSingleInvoice}
                  disabled={isCreatingSingleInvoice || !selectedTemplateInvoiceId || !property.id}
                >
                  {isCreatingSingleInvoice ? 'Creating...' : 'Create Single Property Invoice'}
                </Button>
              </Box>
            </Stack>
          </Paper>
        )}
        
        <Grid container spacing={3}>

          <Grid item xs={12} sm={4}>
            <WalletCard
              purpose="levies"
              property={property}
              outstandingLeviesBalance={outstandingLeviesBalance}
              handleOpenDialog={handleOpenDialog}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            {invoices ? (
              <Card sx={{ 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden'
              }}>
                <InvoicesList property={property} invoices={invoices} />
              </Card>
            ) : (
              <Box>
                <Skeleton variant="rectangular" height={40} width="100%" animation="wave" />
                <Skeleton variant="rectangular" height={40} width="100%" animation="wave" style={{ marginTop: 10 }} />
                <Skeleton variant="rectangular" height={40} width="100%" animation="wave" style={{ marginTop: 10 }} />
              </Box>
            )}
          </Grid>
        </Grid>
      </Stack>

      <Dialog open={openTopUpDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogContent>
          <TopUpModal purpose="levies" propertyId={property.id ?? ""} />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default LeviesSection;
